import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, TrendingUp, Trash2, FileSpreadsheet, ArrowRightLeft, Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';

const Sales = ({ user, branchId, onDataChange }) => {
  const [sales, setSales] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [stokDialogOpen, setStokDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [pastedData, setPastedData] = useState('');
  const [parsedTableData, setParsedTableData] = useState(null);
  const [invalidBranches, setInvalidBranches] = useState([]);
  const [invalidPartais, setInvalidPartais] = useState([]);
  const [bulkSaleDate, setBulkSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [bulkCustomerAccount, setBulkCustomerAccount] = useState('');
  const [bulkKeterangan, setBulkKeterangan] = useState('');
  const [bulkKeteranganBahan, setBulkKeteranganBahan] = useState('');
  const [bulkSaleType, setBulkSaleType] = useState('customer'); // 'customer' or 'branch'
  const [bulkTargetBranch, setBulkTargetBranch] = useState(''); // Target branch for branch-to-branch sale
  const [bulkTargetPartai, setBulkTargetPartai] = useState(''); // Partai for purchase in target branch
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [branchName, setBranchName] = useState('');
  const [importType, setImportType] = useState('penjualan'); // 'penjualan' or 'stok'
  const [editingInvoiceId, setEditingInvoiceId] = useState(null); // Track invoice being edited
  const [editingSaleIds, setEditingSaleIds] = useState([]); // Track sale IDs to delete
  const [selectedSales, setSelectedSales] = useState([]); // For bulk operations
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [activePeriods, setActivePeriods] = useState([]);
  const [selectedTargetPeriod, setSelectedTargetPeriod] = useState('');
  const [convertedSales, setConvertedSales] = useState([]); // Sales from "Jadikan Stok"
  const [normalSales, setNormalSales] = useState([]); // Normal sales
  const [additionalRows, setAdditionalRows] = useState([]); // Manual additional rows
  const [importStokDetailOpen, setImportStokDetailOpen] = useState(false);
  const [selectedImportStok, setSelectedImportStok] = useState(null); // Store import stok transfer details
  const [currentPeriodClosed, setCurrentPeriodClosed] = useState(false); // Track if current period is closed
  // Map of branch_id -> Set of lowercased partai names (from purchases) for real-time partai validation
  const [purchasesByBranch, setPurchasesByBranch] = useState({});
  // Map of branch_id -> Map<lowercased partai, { amount, weight }> (total pembelian per partai)
  const [purchaseTotalsByBranch, setPurchaseTotalsByBranch] = useState({});
  // Map of branch_id -> Map<lowercased partai, total biaya cabang (tanpa biaya operasional otomatis)>
  const [expensesByBranch, setExpensesByBranch] = useState({});
  // Tarif biaya pusat per gram (default periode + override per cabang)
  const [operationalCost, setOperationalCost] = useState({ default: 0, overrides: {} });
  const [purchaseDetailOpen, setPurchaseDetailOpen] = useState(false);
  const [purchaseDetailSearch, setPurchaseDetailSearch] = useState('');

  useEffect(() => {
    fetchData();
    fetchActivePeriods();
    fetchBranches();
    fetchCustomerAccounts();
    fetchPurchasesPartaiMap();
  }, [branchId]);

  usePeriodChange(() => {
    fetchData();
    fetchPurchasesPartaiMap();
  });

  const fetchData = async () => {
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};
      
      // OPTIMIZED: All API calls in parallel for better performance
      const apiCalls = [
        api.get('/sales', { params }),
        api.get('/periods/active').catch(() => null),
        api.get('/customer-accounts').catch(() => ({ data: [] }))
      ];
      
      // Only fetch branch details if branchId is provided
      if (branchId) {
        apiCalls.push(api.get(`/branches/${branchId}`).catch(() => null));
      }
      
      const results = await Promise.all(apiCalls);
      const [salesRes, activePeriodRes, accountsRes, branchRes] = results;
      
      // Filter sales by branchId
      const filteredSales = branchId 
        ? salesRes.data.filter(sale => sale.branch_id === branchId)
        : salesRes.data;
      
      // Separate sales into converted (from Jadikan Stok) and normal
      // Converted sales have customer_name containing "Transfer Stok ke" and keterangan = "Stok"
      const converted = filteredSales.filter(sale => 
        sale.customer_name?.includes('Transfer Stok ke') && sale.keterangan === 'Stok'
      );
      const normal = filteredSales.filter(sale => 
        !(sale.customer_name?.includes('Transfer Stok ke') && sale.keterangan === 'Stok')
      );
      
      setSales(filteredSales);
      setConvertedSales(converted);
      setNormalSales(normal);
      
      // Set branch name
      if (branchId && branchRes) {
        setBranchName(branchRes.data.name);
      } else {
        setBranchName(branchId ? '' : 'Semua Cabang');
      }
      
      // Set active period
      if (activePeriodRes) {
        setActivePeriod(activePeriodRes.data);
      }
      
      // Set customer accounts
      setCustomerAccounts(accountsRes.data);
      
      // Notify parent component to refresh its data (for card totals)
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivePeriods = async () => {
    try {
      const response = await api.get('/periods');
      const currentPeriodId = localStorage.getItem('selectedPeriodId');
      
      // Get current period to compare dates
      const currentPeriod = response.data.find(p => p.id === currentPeriodId);
      
      if (!currentPeriod) {
        setActivePeriods([]);
        setCurrentPeriodClosed(false);
        return;
      }
      
      // Check if current period is closed
      setCurrentPeriodClosed(currentPeriod.is_closed === true);
      
      const currentStartDate = new Date(currentPeriod.start_date);
      
      // Filter: only non-closed periods with start_date AFTER current period (future periods)
      // This is used for both "Pindah Periode" and "Import Stok" features
      const futurePeriods = response.data.filter(p => {
        if (p.is_closed || p.id === currentPeriodId) return false;
        const periodStartDate = new Date(p.start_date);
        return periodStartDate > currentStartDate; // Only future periods
      });
      
      setActivePeriods(futurePeriods);
    } catch (error) {
      console.error('Failed to fetch active periods:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
      console.log('Branches loaded:', response.data.length);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      toast.error('Gagal memuat data cabang');
    }
  };

  const fetchCustomerAccounts = async () => {
    try {
      const response = await api.get('/customer-accounts');
      setCustomerAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch customer accounts:', error);
    }
  };

  // Fetch all purchases for active periods and build a map: branch_id -> Map<lowercased_partai, original_partai>
  // Used for real-time validation and dropdown options in the bulk Sales form. Preserves
  // the ORIGINAL casing of partai names as stored in the Pembelian (Purchases).
  const fetchPurchasesPartaiMap = async () => {
    try {
      // Only include partai from the period currently selected in the main navigation.
      const periodId = localStorage.getItem('selectedPeriodId');
      const params = periodId ? { period_id: periodId } : {};
      const response = await api.get('/purchases', { params });
      const map = {};
      const totals = {};
      (response.data || []).forEach((p) => {
        if (!p.branch_id || !p.partai) return;
        if (!map[p.branch_id]) map[p.branch_id] = new Map();
        const key = String(p.partai).toLowerCase();
        if (!map[p.branch_id].has(key)) {
          map[p.branch_id].set(key, String(p.partai));
        }
        if (!totals[p.branch_id]) totals[p.branch_id] = new Map();
        const prev = totals[p.branch_id].get(key) || { amount: 0, weight: 0 };
        const weight = (p.items || []).reduce((s, it) => s + (parseFloat(it.weight_kg) || 0), 0);
        totals[p.branch_id].set(key, {
          amount: prev.amount + (parseFloat(p.total_amount) || 0),
          weight: prev.weight + weight,
        });
      });
      setPurchasesByBranch(map);
      setPurchaseTotalsByBranch(totals);

      // Biaya cabang per partai (tidak termasuk biaya operasional otomatis, karena
      // biaya pusat dihitung ulang dari berat penjualan pada form) + tarif biaya pusat
      const [expensesRes, settingsRes, periodsRes] = await Promise.all([
        api.get('/expenses', { params }).catch(() => ({ data: [] })),
        api.get('/settings').catch(() => ({ data: {} })),
        api.get('/periods').catch(() => ({ data: [] })),
      ]);
      const expMap = {};
      (expensesRes.data || []).forEach((e) => {
        if (!e.branch_id) return;
        if (String(e.description || '').startsWith('Biaya Operasional - Partai')) return;
        const key = String(e.partai || '').toLowerCase();
        if (!expMap[e.branch_id]) expMap[e.branch_id] = new Map();
        expMap[e.branch_id].set(key, (expMap[e.branch_id].get(key) || 0) + (parseFloat(e.amount) || 0));
      });
      setExpensesByBranch(expMap);

      const currentPeriod = (periodsRes.data || []).find((p) => p.id === periodId);
      const defaultCost = currentPeriod?.default_operational_cost || settingsRes.data?.default_operational_cost || 0;
      const overrideMap = {};
      (settingsRes.data?.branch_operational_overrides || []).forEach((o) => {
        overrideMap[o.branch_id] = o.cost_per_gr;
      });
      setOperationalCost({ default: defaultCost, overrides: overrideMap });
    } catch (error) {
      console.error('Failed to fetch purchases for partai map:', error);
    }
  };


  const toggleSelectSale = (saleId) => {
    setSelectedSales(prev => 
      prev.includes(saleId) 
        ? prev.filter(id => id !== saleId)
        : [...prev, saleId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSales.length === sales.length) {
      setSelectedSales([]);
    } else {
      setSelectedSales(sales.map(s => s.id));
    }
  };

  const handleMoveToPeriod = async () => {
    if (!selectedTargetPeriod) {
      toast.error('Pilih periode tujuan terlebih dahulu');
      return;
    }

    // Validate all selected sales are from the same branch
    const selectedSaleItems = sales.filter(s => selectedSales.includes(s.id));
    const branches = [...new Set(selectedSaleItems.map(s => s.branch_id))];
    if (branches.length > 1) {
      toast.error('Hanya bisa memindahkan penjualan dari cabang yang sama');
      return;
    }

    setLoading(true);
    try {
      await api.post('/sales/move-to-period', {
        sale_ids: selectedSales,
        target_period_id: selectedTargetPeriod
      });
      
      toast.success(`${selectedSales.length} penjualan berhasil dipindahkan ke periode lain`);
      setMoveDialogOpen(false);
      setSelectedSales([]);
      setSelectedTargetPeriod('');
      
      // Refresh data - don't let fetchData errors bubble up
      try {
        await fetchData();
      } catch (fetchError) {
        console.error('Failed to refresh data after move:', fetchError);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memindahkan penjualan');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSale = async (sale) => {
    console.log('handleViewSale called with sale:', {
      id: sale.id,
      keterangan: sale.keterangan,
      customer_name: sale.customer_name,
      isImportStok: sale.keterangan === 'Stok' && sale.customer_name?.includes('Transfer Stok ke')
    });
    
    // Check if this is an Import Stok sale (keterangan === 'Stok')
    if (sale.keterangan === 'Stok' && sale.customer_name && sale.customer_name.includes('Transfer Stok ke')) {
      // This is an Import Stok - show detail dialog with Batalkan button
      console.log('Opening Import Stok detail dialog');
      await handleViewImportStok(sale);
    } else {
      // Normal sale or other - open bulk edit
      console.log('Opening bulk edit dialog');
      handleEditBulkInvoice(sale);
    }
  };

  const handleViewImportStok = async (sale) => {
    try {
      setLoading(true);
      
      // Fetch period transfers to get the import stok details
      const transfersRes = await api.get('/period-transfers');
      const transfers = transfersRes.data;
      
      // Find the IMPORT_STOCK transfer that contains this sale
      const importTransfer = transfers.find(t => 
        t.transfer_type === 'IMPORT_STOCK' && 
        t.entity_ids && 
        t.entity_ids.includes(sale.id)
      );
      
      if (!importTransfer) {
        toast.error('Detail Import Stok tidak ditemukan');
        return;
      }
      
      // Fetch all sales in this import stok batch
      const periodId = localStorage.getItem('selectedPeriodId');
      const allSalesRes = await api.get('/sales', { params: { period_id: periodId } });
      const batchSales = allSalesRes.data.filter(s => importTransfer.entity_ids.includes(s.id));
      
      // Fetch purchases in target period
      const targetPurchasesRes = await api.get('/purchases', { 
        params: { period_id: importTransfer.target_period_id } 
      });
      
      // Filter purchases that match this import (supplier name contains "Transfer dari")
      const targetPurchases = targetPurchasesRes.data.filter(p => 
        p.supplier_name && p.supplier_name.startsWith('Transfer dari') &&
        p.created_by === importTransfer.user_id
      );
      
      setSelectedImportStok({
        transfer: importTransfer,
        sales: batchSales,
        purchases: targetPurchases
      });
      setImportStokDetailOpen(true);
    } catch (error) {
      console.error('Failed to fetch import stok details:', error);
      toast.error('Gagal memuat detail Import Stok');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImportStok = async () => {
    if (!selectedImportStok || !selectedImportStok.transfer) return;
    
    if (!window.confirm('Yakin ingin membatalkan Import Stok ini? Penjualan di periode asal dan pembelian di periode tujuan akan dihapus.')) {
      return;
    }
    
    setLoading(true);
    try {
      await api.delete(`/period-transfers/${selectedImportStok.transfer.id}`);
      toast.success('Import Stok berhasil dibatalkan');
      setImportStokDetailOpen(false);
      setSelectedImportStok(null);
      fetchData();
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Failed to cancel import stok:', error);
      toast.error('Gagal membatalkan Import Stok: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditBulkInvoice = async (clickedSale) => {
    try {
      console.log('=== HANDLE EDIT BULK INVOICE START ===');
      console.log('Clicked Sale Data:', {
        id: clickedSale.id,
        invoice_id: clickedSale.invoice_id,
        product_name: clickedSale.product_name,
        is_branch_to_branch: clickedSale.is_branch_to_branch,
        target_branch_id: clickedSale.target_branch_id,
        target_partai: clickedSale.target_partai,
        customer_account_id: clickedSale.customer_account_id,
        keterangan: clickedSale.keterangan
      });
      
      // Fetch ALL sales for this invoice (not filtered by branch)
      const periodId = localStorage.getItem('selectedPeriodId');
      const params = periodId ? { period_id: periodId } : {};
      
      const allSalesRes = await api.get('/sales', { params });
      const allSales = allSalesRes.data;
      
      // Find all sales for this specific invoice
      let invoiceSales;
      
      // Determine import type based on keterangan
      const isStok = clickedSale.keterangan === 'Stok';
      setImportType(isStok ? 'stok' : 'penjualan');
      
      if (clickedSale.invoice_id) {
        // New method: Filter by invoice_id (most reliable)
        invoiceSales = allSales.filter(s => s.invoice_id === clickedSale.invoice_id);
      } else {
        // Fallback for old data
        const clickedDate = clickedSale.sale_date?.split('T')[0];
        if (isStok) {
          // For stok, each entry can be separate or can be grouped by date
          // Let's group by date to allow editing multiple stok entries at once
          invoiceSales = allSales.filter(s => 
            s.keterangan === 'Stok' &&
            s.sale_date?.split('T')[0] === clickedDate &&
            s.branch_id === clickedSale.branch_id  // Same branch
          );
        } else {
          // For regular sales: Filter by customer_account_id, keterangan, AND sale_date
          invoiceSales = allSales.filter(s => 
            s.customer_account_id === clickedSale.customer_account_id &&
            s.keterangan === clickedSale.keterangan &&
            s.keterangan !== 'Stok' &&
            s.sale_date?.split('T')[0] === clickedDate  // Same date
          );
        }
      }

      if (invoiceSales.length === 0) {
        toast.error('Tidak ada data untuk diedit');
        return;
      }
      
      console.log('Invoice Sales Found:', invoiceSales.length);
      console.log('All sales in this invoice:');
      invoiceSales.forEach((s, idx) => {
        console.log(`  [${idx}] ${s.product_name} - is_b2b: ${s.is_branch_to_branch}, target: ${s.target_branch_id?.substring(0,10)}, partai: ${s.target_partai}`);
      });

      // IMPORTANT: Use clickedSale metadata, NOT invoiceSales[0]
      // Because all sales in invoice have same metadata
      const saleMetadata = clickedSale;
      
      // Set bulk form values FROM CLICKED SALE
      setBulkSaleDate(saleMetadata.sale_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
      
      console.log('=== SETTING FORM VALUES ===');
      console.log('Using metadata from clicked sale:', {
        is_branch_to_branch: saleMetadata.is_branch_to_branch,
        target_branch_id: saleMetadata.target_branch_id,
        target_partai: saleMetadata.target_partai,
        customer_account_id: saleMetadata.customer_account_id,
        keterangan: saleMetadata.keterangan
      });
      
      // Detect if this is branch-to-branch sale
      // Repopulate "Keterangan Bahan" from the invoice items (per-item note)
      const firstBahanKet = invoiceSales.find(s => s.keterangan && s.keterangan !== 'Stok')?.keterangan || '';
      if (saleMetadata.is_branch_to_branch) {
        console.log('✅ Setting to BRANCH sale type');
        setBulkSaleType('branch');
        setBulkTargetBranch(saleMetadata.target_branch_id || '');
        setBulkTargetPartai(saleMetadata.target_partai || '');
        setBulkCustomerAccount('');
        setBulkKeterangan('');
        setBulkKeteranganBahan(firstBahanKet);
      } else {
        console.log('✅ Setting to CUSTOMER sale type');
        setBulkSaleType('customer');
        setBulkCustomerAccount(saleMetadata.customer_account_id || '');
        setBulkKeterangan(saleMetadata.keterangan_rekening || invoiceSales.find(s => s.keterangan_rekening)?.keterangan_rekening || '');
        setBulkKeteranganBahan(firstBahanKet);
        setBulkTargetBranch('');
        setBulkTargetPartai('');
      }

      // Build PARALLEL arrays for branches and partais (not nested)
      // Each column is one branch-partai combination
      const branchPartaiPairs = [];
      const branchPartaiMap = new Map(); // To track unique combinations and their indices
      
      // Fetch branch names for displaying
      const branchesRes = await api.get('/branches');
      const allBranches = branchesRes.data;
      const branchIdToName = {};
      allBranches.forEach(b => {
        branchIdToName[b.id] = b.name;
      });
      
      invoiceSales.forEach(sale => {
        const branchName = branchIdToName[sale.branch_id] || sale.branch_name || 'Cabang';
        const partai = sale.partai || 'Tanpa Partai';
        const key = `${branchName}_${partai}`;
        
        if (!branchPartaiMap.has(key)) {
          branchPartaiMap.set(key, branchPartaiPairs.length);
          branchPartaiPairs.push({ branch: branchName, partai: partai });
        }
      });
      
      // Extract parallel arrays
      const branches = branchPartaiPairs.map(p => p.branch);
      const partais = branchPartaiPairs.map(p => p.partai);
      
      // Group by product_name and build weights array
      const materialsMap = new Map();
      
      invoiceSales.forEach(sale => {
        const key = `${sale.product_name}`;
        if (!materialsMap.has(key)) {
          materialsMap.set(key, {
            name: sale.product_name,
            price: sale.price_per_kg,
            weights: new Array(branchPartaiPairs.length).fill(0), // Initialize with correct length
            saleIds: []
          });
        }
        
        const material = materialsMap.get(key);
        const branchName = branchIdToName[sale.branch_id] || sale.branch_name || 'Cabang';
        const partai = sale.partai || 'Tanpa Partai';
        const weightKey = `${branchName}_${partai}`;
        const colIdx = branchPartaiMap.get(weightKey);
        
        material.weights[colIdx] = (material.weights[colIdx] || 0) + Math.round(sale.quantity_kg);
        material.saleIds.push(sale.id);
      });

      const materials = Array.from(materialsMap.values());

      // Set parsed table data with parallel structure
      setParsedTableData({
        materials: materials,
        branches: branches,
        partais: partais
      });

      // Set editing state
      setEditingInvoiceId(clickedSale.invoice_id || null);
      setEditingSaleIds(invoiceSales.map(s => s.id));

      // Open preview dialog to show the form
      setPreviewDialogOpen(true);
      
      const uniqueBranches = [...new Set(branches)];
      const typeLabel = isStok ? 'Stok' : 'Invoice';
      const invoiceLabel = clickedSale.invoice_id ? 'Invoice ID' : 'tanggal yang sama';
      toast.info(`Editing ${typeLabel}: ${invoiceSales.length} items, ${branchPartaiPairs.length} kolom dari ${uniqueBranches.length} cabang (${invoiceLabel})`);
    } catch (error) {
      toast.error('Gagal memuat data');
      console.error('Error loading data:', error);
    }
  };

  const handleDeleteBulkInvoice = async () => {
    if (editingSaleIds.length === 0) {
      toast.error('Tidak ada data untuk dihapus');
      return;
    }

    const confirmMessage = importType === 'stok' 
      ? `Yakin ingin menghapus ${editingSaleIds.length} data stok ini?`
      : `Yakin ingin menghapus invoice dengan ${editingSaleIds.length} items ini?`;
    
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      // Use bulk delete endpoint - creates only 1 transaction and 1 activity log
      await api.post('/sales/bulk-delete', editingSaleIds);
      
      toast.success(importType === 'stok' 
        ? `${editingSaleIds.length} stok berhasil dihapus`
        : `Invoice dengan ${editingSaleIds.length} items berhasil dihapus`
      );
      
      // Reset state and close dialog
      setPreviewDialogOpen(false);
      setParsedTableData(null);
      setEditingInvoiceId(null);
      setEditingSaleIds([]);
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertUlang = () => {
    // Close preview dialog and open paste dialog for re-import
    setPreviewDialogOpen(false);
    // Keep editing state (editingInvoiceId, editingSaleIds)
    // Open appropriate dialog based on import type
    if (importType === 'stok') {
      setStokDialogOpen(true);
    } else {
      setBulkDialogOpen(true);
    }
    // Clear parsed data to start fresh
    setParsedTableData(null);
    setPastedData('');
    toast.info('Paste data baru untuk mengupdate invoice');
  };

  const handleDelete = async () => {
    if (!selectedSale) return;
    
    if (!window.confirm('Yakin ingin menghapus data penjualan ini?')) return;

    setLoading(true);
    try {
      await api.delete(`/sales/${selectedSale.id}`);
      toast.success('Penjualan berhasil dihapus');
      setViewDialogOpen(false);
      setSelectedSale(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operasi gagal');
    } finally {
      setLoading(false);
    }
  };

  const parseNumber = (str) => {
    if (str === null || str === undefined) return 0;
    const s = String(str).trim();
    if (!s) return 0;
    // Detect negative sign in multiple common formats:
    //   "-150", "- 150"      → leading minus
    //   "(150)" / "( 150 )"  → Excel/accounting parentheses
    //   "150-"               → trailing minus (some locales)
    //   "Rp -150" / "-Rp 150" → with currency prefix
    const isNegative =
      /^\s*-/.test(s) ||                    // leading minus (after optional whitespace)
      /^\s*\(.+\)\s*$/.test(s) ||           // wrapped in parentheses
      /-\s*$/.test(s) ||                    // trailing minus
      /[A-Za-z]+\s*-/.test(s);              // currency-prefix then minus, e.g., "Rp -150"
    // Strip everything except digits → safe numeric core
    const digits = s.replace(/[^\d]/g, '');
    if (!digits) return 0;
    const num = parseFloat(digits);
    return isNegative ? -num : num;
  };

  const handleConvertData = () => {
    if (!pastedData.trim()) {
      toast.error('Silakan paste data terlebih dahulu');
      return;
    }

    try {
      // Parse tab-separated values
      const lines = pastedData.trim().split('\n');
      if (lines.length < 3) {
        toast.error('Data tidak lengkap. Minimal 3 baris: cabang, partai, dan bahan');
        return;
      }

      // Parse rows
      const rows = lines.map(line => line.split('\t').map(cell => cell.trim()));
      
      // Row 1: Branch names (skip first column = "Cabang" and last column = "Harga")
      const branches = rows[0].slice(1, -1);
      
      // Row 2: Partai names (skip first and last column)
      const partais = rows[1].slice(1, -1);
      
      // Validate that branches and partais have same length
      if (branches.length !== partais.length) {
        toast.error('Jumlah kolom cabang dan partai tidak sama');
        return;
      }
      
      // Row 3+: Material data
      const materials = [];
      for (let i = 2; i < rows.length; i++) {
        const row = rows[i];
        if (row[0]) { // Has material name
          // Parse price - remove thousand separator (last column)
          const priceRaw = row[row.length - 1] || '0';
          const price = parseNumber(priceRaw);
          
          // Parse weights for all data columns (skip first = material name, and last = price)
          const weightsRaw = row.slice(1, -1);
          const weights = weightsRaw.map(w => parseNumber(w));
          
          // Validate weights length matches branches length
          if (weights.length !== branches.length) {
            console.warn(`Row ${i}: weights length (${weights.length}) != branches length (${branches.length})`);
          }
          
          materials.push({
            name: row[0],
            weights: weights,
            price: price
          });
        }
      }

      setParsedTableData({
        branches: branches,
        partais: partais,
        materials: materials
      });
      
      // Don't reset editing state - it will be preserved from handleConvertUlang
      // If this is a fresh convert (not from Convert Ulang button), editing state is already null/empty
      
      console.log('Parsed data:', {
        branches: branches.length,
        partais: partais.length,
        materials: materials.length,
        editMode: editingSaleIds.length > 0
      });
      
      // Close the appropriate dialog
      if (importType === 'stok') {
        setStokDialogOpen(false);
      } else {
        setBulkDialogOpen(false);
      }
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Convert error:', error);
      toast.error('Gagal memproses data. Pastikan format sudah benar');
    }
  };

  const handlePriceChange = (index, value) => {
    const updated = {...parsedTableData};
    updated.materials[index].price = value;
    setParsedTableData(updated);
  };

  const handleBranchNameChange = (index, value) => {
    const updated = {...parsedTableData};
    updated.branches[index] = value;
    setParsedTableData(updated);
    // Clear invalid state for this branch if it was edited
    setInvalidBranches(prev => prev.filter(b => b !== updated.branches[index]));
  };

  const handlePartaiNameChange = (index, value) => {
    const updated = {...parsedTableData};
    updated.partais[index] = value;
    setParsedTableData(updated);
    // Clear invalid state for this partai if it was edited
    setInvalidPartais(prev => prev.filter(p => p !== updated.partais[index]));
  };

  // Real-time validator: returns true if the partai at column `idx` does NOT exist in
  // the purchases of the branch in the same column. Empty partai is considered valid.
  const isPartaiInvalidForColumn = (idx) => {
    if (!parsedTableData) return false;
    const partaiRaw = (parsedTableData.partais[idx] || '').trim();
    const branchNameRaw = (parsedTableData.branches[idx] || '').trim();
    if (!partaiRaw) return false; // empty partai allowed
    // If the purchases map hasn't loaded yet at all, skip validation to avoid false positives
    if (!purchasesByBranch || Object.keys(purchasesByBranch).length === 0) return false;
    // Find branch by name (case-insensitive)
    const branch = branches.find(b => b.name && b.name.toLowerCase() === branchNameRaw.toLowerCase());
    if (!branch) return false; // invalid branch is handled separately
    const partaiMap = purchasesByBranch[branch.id];
    if (!partaiMap || partaiMap.size === 0) return true;
    return !partaiMap.has(partaiRaw.toLowerCase());
  };

  // True if ANY partai column is invalid (used to block submit)
  const hasInvalidPartaiInForm = () => {
    if (!parsedTableData) return false;
    return parsedTableData.partais.some((_, idx) => isPartaiInvalidForColumn(idx));
  };

  const calculateTotalKg = (weights) => {
    return weights.reduce((sum, weight) => {
      const w = parseFloat(weight) || 0;
      return sum + w;
    }, 0);
  };

  const calculateSubtotal = (weights, price) => {
    const totalKg = calculateTotalKg(weights);
    const priceNum = parseFloat(price) || 0;
    return totalKg * priceNum;
  };

  const calculateGrandTotal = () => {
    if (!parsedTableData) return 0;
    return parsedTableData.materials.reduce((sum, material) => {
      return sum + calculateSubtotal(material.weights, material.price);
    }, 0);
  };

  const calculateTotalWeightPerPartai = (colIndex) => {
    if (!parsedTableData) return 0;
    return parsedTableData.materials.reduce((sum, material) => {
      const weight = parseFloat(material.weights[colIndex]) || 0;
      return sum + weight;
    }, 0);
  };

  // Additional rows helpers
  const handleAddRow = () => {
    setAdditionalRows(prev => [...prev, { branchName: '', partai: '', bahan: '', nominal: '' }]);
  };

  const handleRemoveRow = (index) => {
    setAdditionalRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdditionalRowChange = (index, field, value) => {
    setAdditionalRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const calculateAdditionalTotal = () => {
    return additionalRows.reduce((sum, row) => {
      const nominal = parseFloat(String(row.nominal).replace(/\./g, '').replace(/[^\d-]/g, '')) || 0;
      return sum + nominal;
    }, 0);
  };

  const calculateCombinedGrandTotal = () => {
    return calculateGrandTotal() + calculateAdditionalTotal();
  };

  // Total pembelian (Rp & berat) dari seluruh partai unik yang dipilih di form penjualan.
  // Dihitung ulang otomatis setiap kali partai/cabang pada kolom atau baris tambahan berubah.
  const calculatePurchaseSummary = () => {
    const rows = new Map(); // branch.id|partai -> row

    const addPair = (branchName, partai) => {
      const bName = (branchName || '').trim().toLowerCase();
      const pKey = (partai || '').trim().toLowerCase();
      if (!bName || !pKey) return null;
      const branch = branches.find((b) => b.name && b.name.toLowerCase() === bName);
      if (!branch) return null;
      const key = `${branch.id}|${pKey}`;
      if (!rows.has(key)) {
        const entry = purchaseTotalsByBranch[branch.id]?.get(pKey);
        rows.set(key, {
          key,
          branchName: branch.name,
          partai: (partai || '').trim(),
          weight: entry?.weight || 0,
          amount: entry?.amount || 0,
          biaya: expensesByBranch[branch.id]?.get(pKey) || 0,
        });
      }
      return { row: rows.get(key), branch };
    };

    if (parsedTableData) {
      parsedTableData.branches.forEach((branchName, idx) => {
        const res = addPair(branchName, parsedTableData.partais[idx]);
        if (!res) return;
        // Biaya pusat yang dibebankan ke cabang: berat penjualan kolom x tarif cabang
        const rate = operationalCost.overrides[res.branch.id] !== undefined
          ? operationalCost.overrides[res.branch.id]
          : operationalCost.default;
        const colWeight = parsedTableData.materials.reduce((s, m) => s + (parseFloat(m.weights[idx]) || 0), 0);
        res.row.biaya += colWeight * (rate || 0);
      });
    }
    additionalRows.forEach((row) => addPair(row.branchName, row.partai));

    const list = Array.from(rows.values());
    const amount = list.reduce((s, r) => s + r.amount, 0);
    const weight = list.reduce((s, r) => s + r.weight, 0);
    const biaya = list.reduce((s, r) => s + r.biaya, 0);

    return { amount, weight, biaya, total: amount + biaya, rows: list };
  };

  // Total berat penjualan dari seluruh kolom pada form
  const calculateTotalSalesWeight = () => {
    if (!parsedTableData) return 0;
    return parsedTableData.materials.reduce(
      (sum, material) => sum + (material.weights || []).reduce((s, w) => s + (parseFloat(w) || 0), 0),
      0
    );
  };

  const handleSubmitBulk = async () => {
    try {
      // Validate based on sale type
      if (importType === 'penjualan') {
        if (bulkSaleType === 'customer') {
          if (!bulkCustomerAccount) {
            toast.error('Rekening Customer harus dipilih');
            return;
          }
          if (!bulkKeterangan || !bulkKeterangan.trim()) {
            toast.error('Keterangan untuk history rekening harus diisi');
            return;
          }
        } else if (bulkSaleType === 'branch') {
          if (!bulkTargetBranch) {
            toast.error('Cabang tujuan harus dipilih');
            return;
          }
          // Partai is optional, no validation needed
        }
      }

      // Validate target period is selected ONLY for stok
      if (importType === 'stok' && !selectedTargetPeriod) {
        toast.error('Periode tujuan harus dipilih');
        return;
      }

      // Check if current period is closed - block submission
      if (currentPeriodClosed) {
        toast.error('Periode saat ini sudah ditutup. Tidak dapat menambah penjualan ke periode yang sudah ditutup.');
        return;
      }

      // Validate branch names exist in database and create mapping
      const branchesRes = await api.get('/branches');
      const allBranches = branchesRes.data;
      const branchNames = allBranches.map(b => b.name.toLowerCase());
      
      // Create branch name to ID mapping (case-insensitive)
      const branchNameToId = {};
      allBranches.forEach(branch => {
        branchNameToId[branch.name.toLowerCase()] = branch.id;
      });
      
      const invalidBranchList = parsedTableData.branches.filter(
        branch => !branchNames.includes(branch.toLowerCase())
      );
      
      // Validate partai per-branch column: each partai must exist in purchases of the branch in the same column.
      // Scope to the currently selected period so submit-time validation matches the dropdown/real-time validation.
      const submitPeriodId = localStorage.getItem('selectedPeriodId');
      const purchasesRes = await api.get('/purchases', { params: submitPeriodId ? { period_id: submitPeriodId } : {} });
      const partaiByBranch = {};
      (purchasesRes.data || []).forEach(p => {
        if (!p.branch_id || !p.partai) return;
        if (!partaiByBranch[p.branch_id]) partaiByBranch[p.branch_id] = new Map();
        const key = String(p.partai).toLowerCase();
        if (!partaiByBranch[p.branch_id].has(key)) {
          partaiByBranch[p.branch_id].set(key, String(p.partai));
        }
      });
      // Keep the live map in sync as well
      setPurchasesByBranch(partaiByBranch);
      
      const invalidPartaiList = [];
      parsedTableData.partais.forEach((partai, idx) => {
        if (!partai) return; // empty allowed
        const branchName = (parsedTableData.branches[idx] || '').toLowerCase();
        const targetBranchId = branchNameToId[branchName];
        if (!targetBranchId) return; // branch invalid handled separately
        const partaiMap = partaiByBranch[targetBranchId];
        if (!partaiMap || !partaiMap.has(String(partai).toLowerCase())) {
          invalidPartaiList.push(partai);
        }
      });
      
      // Set invalid states for visual highlighting
      setInvalidBranches(invalidBranchList);
      setInvalidPartais(invalidPartaiList);
      
      if (invalidBranchList.length > 0 || invalidPartaiList.length > 0) {
        const errors = [];
        if (invalidBranchList.length > 0) {
          errors.push(`Cabang tidak ditemukan: ${invalidBranchList.join(', ')}`);
        }
        if (invalidPartaiList.length > 0) {
          errors.push(`Partai tidak ditemukan: ${invalidPartaiList.join(', ')}`);
        }
        toast.error(errors.join(' | '));
        return;
      }

      // Create sales entries for each material-branch combination
      const salesData = [];
      // Get current period from localStorage (for sales records)
      const currentPeriodId = localStorage.getItem('selectedPeriodId');
      // Get target period from selector (for purchase records in import stock)
      const targetPeriodId = selectedTargetPeriod;
      
      parsedTableData.materials.forEach((material, materialIdx) => {
        material.weights.forEach((weight, idx) => {
          // Allow both positive and negative weights (skip only if weight is 0 or empty)
          if (weight && parseFloat(weight) !== 0) {
            const branchName = parsedTableData.branches[idx] || '';
            const targetBranchId = branchNameToId[branchName.toLowerCase()];
            
            const saleData = {
              branch_id: targetBranchId, // Use branch_id from the column name
              product_name: material.name,
              partai: parsedTableData.partais[idx] || '',
              customer_name: branchName,
              sale_date: bulkSaleDate,
              quantity_kg: parseFloat(weight),
              price_per_kg: parseFloat(material.price),
              // For 'stok': sales use CURRENT period, purchases use TARGET period
              // For 'penjualan': sales use CURRENT period only
              period_id: currentPeriodId || null,
              sort_order: materialIdx  // Preserve original order from import table
            };

            // Add keterangan and customer account based on import type
            if (importType === 'stok') {
              saleData.keterangan = ''; // Blank for item
              // No customer_account_id for stock
            } else if (importType === 'penjualan') {
              // "Keterangan Bahan" fills every item's keterangan (per-material note)
              saleData.keterangan = bulkKeteranganBahan || '';
              if (bulkSaleType === 'customer') {
                saleData.customer_account_id = bulkCustomerAccount;
              } else if (bulkSaleType === 'branch') {
                // For branch-to-branch sale, no customer account
                saleData.customer_account_id = null;
              }
            }

            salesData.push(saleData);
          }
        });
      });

      // Add additional manual rows to salesData
      for (const row of additionalRows) {
        if (!row.branchName || !row.nominal) continue;
        const nominalValue = parseFloat(String(row.nominal).replace(/\./g, '').replace(/[^\d-]/g, '')) || 0;
        if (nominalValue === 0) continue;
        
        const targetBranchId = branchNameToId[row.branchName.toLowerCase()];
        if (!targetBranchId) {
          // Check if branch name is valid
          if (!branchNames.includes(row.branchName.toLowerCase())) {
            setInvalidBranches(prev => [...prev, row.branchName]);
            toast.error(`Cabang tambahan tidak ditemukan: ${row.branchName}`);
            return;
          }
        }
        
        salesData.push({
          branch_id: targetBranchId,
          product_name: row.bahan || 'Penjualan Tambahan',
          partai: row.partai || '',
          customer_name: row.branchName,
          sale_date: bulkSaleDate,
          quantity_kg: 1,
          price_per_kg: nominalValue,
          period_id: currentPeriodId || null,
          keterangan: importType === 'penjualan' ? (bulkKeteranganBahan || '') : '',
          sort_order: parsedTableData.materials.length + additionalRows.indexOf(row),
          ...(importType === 'penjualan' && bulkSaleType === 'customer' 
            ? { customer_account_id: bulkCustomerAccount } 
            : { customer_account_id: null })
        });
      }

      setLoading(true);

      // Check if this is update or create mode
      if (editingSaleIds.length > 0) {
        // UPDATE MODE
        if (importType === 'penjualan' && bulkSaleType === 'branch') {
          // Branch-to-branch update: delete old and create new
          // This is simpler and ensures purchases are properly recreated
          await api.post('/sales/bulk-delete', editingSaleIds);
          
          const response = await api.post('/sales/branch-to-branch', {
            sales_data: salesData,
            target_branch_id: bulkTargetBranch,
            target_partai: bulkTargetPartai,
            keterangan: bulkKeterangan || `Transfer ke ${branches.find(b => b.id === bulkTargetBranch)?.name || 'Cabang'}`,
            source_branch_id: branchId
          });
          
          toast.success(`Invoice berhasil diupdate: ${response.data.sales_created} penjualan dan ${response.data.purchases_created} pembelian`);
        } else {
          // Regular update (customer sale or stok)
          const updatePayload = {
            old_sale_ids: editingSaleIds,
            invoice_id: editingInvoiceId,
            new_sales: salesData,
            keterangan: bulkKeterangan // Add keterangan for customer sales
          };
          
          const response = await api.post('/sales/bulk-update', updatePayload);
          
          const message = importType === 'stok' 
            ? `Stok berhasil diupdate (${response.data.items_created} items)`
            : `Invoice berhasil diupdate (${response.data.items_created} items, Selisih: ${formatCurrency(response.data.difference)})`;
          toast.success(message);
        }
      } else {
        // CREATE MODE
        if (importType === 'stok') {
          // Use import-stock endpoint for stock import
          const response = await api.post('/sales/import-stock', {
            sales_data: salesData,
            target_period_id: targetPeriodId
          });
          
          toast.success(
            `${response.data.sales_count} penjualan dan ${response.data.purchases_count} pembelian berhasil dibuat. ` +
            `Stok ditransfer dari ${response.data.current_period} ke ${response.data.target_period}`
          );
        } else {
          // Use bulk create endpoint for regular sales
          if (bulkSaleType === 'branch') {
            // Branch-to-branch sale: create sales + purchases
            const response = await api.post('/sales/branch-to-branch', {
              sales_data: salesData,
              target_branch_id: bulkTargetBranch,
              target_partai: bulkTargetPartai,
              keterangan: bulkKeterangan || `Transfer ke ${branches.find(b => b.id === bulkTargetBranch)?.name || 'Cabang'}`,
              source_branch_id: branchId
            });
            toast.success(`${response.data.sales_created} penjualan dan ${response.data.purchases_created} pembelian berhasil dibuat`);
          } else {
            // Regular customer sale - keterangan blank for items
            // Backend will use keterangan from customer_account_id transaction
            await api.post('/sales/bulk', {
              sales_data: salesData,
              keterangan: bulkKeterangan // Send keterangan separately for transaction history
            });
            toast.success(`${salesData.length} penjualan berhasil ditambahkan (1 invoice)`);
          }
        }
      }
      
      setPreviewDialogOpen(false);
      setPastedData('');
      setParsedTableData(null);
      setInvalidBranches([]);
      setInvalidPartais([]);
      setEditingInvoiceId(null);
      setEditingSaleIds([]);
      setSelectedTargetPeriod('');
      setAdditionalRows([]);
      fetchData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data penjualan');
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount).replace('Rp', 'Rp.');
  };

  // Helper for thousand separator in input
  const formatNumber = (value) => {
    if (!value) return '';
    const numericValue = value.toString().replace(/[^\d.]/g, '');
    return new Intl.NumberFormat('id-ID').format(numericValue);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading && sales.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-section">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {/* Single item dialog removed - only bulk sales available */}

        {/* Bulk Import Penjualan Dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              className="border-teal-600 text-teal-600 hover:bg-teal-50"
              onClick={() => {
                setImportType('penjualan');
                // Reset editing state when opening fresh import dialog
                setEditingInvoiceId(null);
                setEditingSaleIds([]);
                setBulkKeterangan('');
                setBulkKeteranganBahan('');
              }}
            >
              <FileSpreadsheet size={20} className="mr-2" />
              Import Penjualan
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Penjualan dari Tabel</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Paste Data dari Excel/Spreadsheet</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Format: Baris 1 = Nama Cabang | Kolom terakhir = Harga<br/>
                  Baris 2 = Partai, Baris 3+ = Nama Bahan | Berat 1 | Berat 2 | ... | Harga
                </p>
                <Textarea
                  value={pastedData}
                  onChange={(e) => setPastedData(e.target.value)}
                  placeholder="Paste data di sini..."
                  className="min-h-[200px] font-mono text-sm"
                  autoComplete="off"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Batal</Button>
                <Button onClick={handleConvertData} className="bg-teal-600 hover:bg-teal-700">
                  Convert ke Tabel
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Stok Dialog */}
        <Dialog open={stokDialogOpen} onOpenChange={setStokDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              className="border-orange-600 text-orange-600 hover:bg-orange-50"
              onClick={() => {
                setImportType('stok');
                // Reset editing state when opening fresh import dialog
                setEditingInvoiceId(null);
                setEditingSaleIds([]);
                setSelectedTargetPeriod('');  // Reset period selector
              }}
            >
              <FileSpreadsheet size={20} className="mr-2" />
              Import Stok
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Stok dari Tabel</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Paste Data dari Excel/Spreadsheet</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Format: Baris 1 = Nama Cabang | Kolom terakhir = Harga<br/>
                  Baris 2 = Partai, Baris 3+ = Nama Bahan | Berat 1 | Berat 2 | ... | Harga
                </p>
                <Textarea
                  value={pastedData}
                  onChange={(e) => setPastedData(e.target.value)}
                  placeholder="Paste data di sini..."
                  className="min-h-[200px] font-mono text-sm"
                  autoComplete="off"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStokDialogOpen(false)}>Batal</Button>
                <Button onClick={handleConvertData} className="bg-orange-600 hover:bg-orange-700">
                  Convert ke Tabel
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview & Edit Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>Form Penjualan</DialogTitle>
            </DialogHeader>
            {parsedTableData && (
              <div className="space-y-3 flex-1 overflow-y-auto pr-1">
                {/* Date, Customer Account, and Keterangan Form - Inline */}
                <div className={`grid ${importType === 'penjualan' ? 'grid-cols-3' : 'grid-cols-2'} gap-4 p-4 bg-slate-50 rounded-lg border`}>
                  <div className="flex flex-col">
                    <Label htmlFor="bulk_sale_date" className="text-xs font-semibold mb-2 block">
                      Tanggal {importType === 'stok' ? 'Stok' : 'Penjualan'} *
                    </Label>
                    <Input
                      id="bulk_sale_date"
                      type="date"
                      value={bulkSaleDate}
                      onChange={(e) => setBulkSaleDate(e.target.value)}
                      className="text-xs"
                      required
                    />
                  </div>
                  {importType === 'stok' && (
                    <div className="flex flex-col">
                      <Label htmlFor="stok_target_period" className="text-xs font-semibold mb-2 block">
                        Pilih Periode Tujuan *
                      </Label>
                      <Select 
                        value={selectedTargetPeriod} 
                        onValueChange={setSelectedTargetPeriod}
                      >
                        <SelectTrigger id="stok_target_period" className="text-xs">
                          <SelectValue placeholder="Pilih periode..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activePeriods.length === 0 ? (
                            <SelectItem value="none" disabled>Tidak ada periode masa depan</SelectItem>
                          ) : (
                            activePeriods.map(period => (
                              <SelectItem key={period.id} value={period.id}>
                                {period.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {importType === 'penjualan' && (
                    <>
                      {/* Radio button untuk pilih tujuan */}
                      <div className="flex flex-col col-span-2">
                        <Label className="text-xs font-semibold mb-2 block">Tujuan Penjualan *</Label>
                        <div className="flex gap-6">
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id="sale_to_customer"
                              name="sale_type"
                              value="customer"
                              checked={bulkSaleType === 'customer'}
                              onChange={(e) => {
                                setBulkSaleType(e.target.value);
                                setBulkTargetBranch('');
                                setBulkTargetPartai('');
                              }}
                              className="w-4 h-4 text-teal-600"
                            />
                            <Label htmlFor="sale_to_customer" className="text-xs font-normal cursor-pointer">
                              Jual ke Customer
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id="sale_to_branch"
                              name="sale_type"
                              value="branch"
                              checked={bulkSaleType === 'branch'}
                              onChange={(e) => {
                                setBulkSaleType(e.target.value);
                                setBulkCustomerAccount('');
                              }}
                              className="w-4 h-4 text-teal-600"
                            />
                            <Label htmlFor="sale_to_branch" className="text-xs font-normal cursor-pointer">
                              Jual ke Cabang
                            </Label>
                          </div>
                        </div>
                      </div>

                      {/* Customer fields (if sale to customer) */}
                      {bulkSaleType === 'customer' && (
                        <>
                          <div className="flex flex-col">
                            <Label htmlFor="bulk_customer_account" className="text-xs font-semibold mb-2 block">Nama Customer *</Label>
                            <SearchableSelect
                              value={bulkCustomerAccount}
                              onValueChange={setBulkCustomerAccount}
                              placeholder="Pilih Customer"
                              emptyText="Customer tidak ditemukan"
                              options={customerAccounts.map(account => ({
                                value: account.id,
                                label: `${account.account_name}${account.bank_name ? ' - ' + account.bank_name : ''}`
                              }))}
                              className="w-full"
                              triggerClassName="text-xs"
                            />
                          </div>
                          <div className="flex flex-col">
                            <Label htmlFor="bulk_keterangan" className="text-xs font-semibold mb-2 block">
                              Keterangan Rekening *
                            </Label>
                            <Input
                              id="bulk_keterangan"
                              type="text"
                              value={bulkKeterangan}
                              onChange={(e) => setBulkKeterangan(e.target.value)}
                              className="text-xs"
                              placeholder="Keterangan untuk history transaksi rekening"
                              data-testid="bulk-keterangan-rekening-input"
                              required
                            />
                          </div>
                          <div className="flex flex-col">
                            <Label htmlFor="bulk_keterangan_bahan" className="text-xs font-semibold mb-2 block">
                              Keterangan Bahan
                            </Label>
                            <Input
                              id="bulk_keterangan_bahan"
                              type="text"
                              value={bulkKeteranganBahan}
                              onChange={(e) => setBulkKeteranganBahan(e.target.value)}
                              className="text-xs"
                              placeholder="Jika diisi, mengisi keterangan semua bahan"
                              data-testid="bulk-keterangan-bahan-input"
                            />
                          </div>
                        </>
                      )}

                      {/* Branch fields (if sale to branch) */}
                      {bulkSaleType === 'branch' && (
                        <>
                          <div className="flex flex-col">
                            <Label htmlFor="bulk_target_branch" className="text-xs font-semibold mb-2 block">Cabang Tujuan *</Label>
                            <SearchableSelect
                              value={bulkTargetBranch}
                              onValueChange={setBulkTargetBranch}
                              placeholder="Pilih Cabang Tujuan"
                              emptyText={branches.length === 0 ? "Memuat cabang..." : "Cabang tidak ditemukan"}
                              options={branches.filter(b => b.id !== branchId).map(branch => ({
                                value: branch.id,
                                label: branch.name
                              }))}
                              className="w-full"
                              triggerClassName="text-xs"
                            />
                            {branches.length === 0 && (
                              <p className="text-xs text-amber-600 mt-1">Loading branches...</p>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <Label htmlFor="bulk_target_partai" className="text-xs font-semibold mb-2 block">
                              Partai (Untuk Pembelian Cabang Tujuan)
                            </Label>
                            <Input
                              id="bulk_target_partai"
                              type="text"
                              value={bulkTargetPartai}
                              onChange={(e) => setBulkTargetPartai(e.target.value)}
                              className="text-xs"
                              placeholder="Opsional - Nama partai untuk pembelian di cabang tujuan"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="overflow-auto max-h-[55vh]">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-1 px-2 text-xs font-semibold bg-slate-100 sticky left-0 z-10 whitespace-nowrap">Bahan</TableHead>
                        {parsedTableData.branches.map((branch, idx) => {
                          const partai = parsedTableData.partais[idx] || '';
                          const isBranchInvalid = invalidBranches.includes(branch);
                          // Per-column live partai validation: invalid if partai not found in this branch's purchases
                          const isPartaiInvalid = isPartaiInvalidForColumn(idx) || invalidPartais.includes(partai);
                          // Partai column highlighted in red separately from branch column
                          const branchBgClass = isBranchInvalid ? 'bg-red-200' : '';
                          const partaiBgClass = isPartaiInvalid ? 'bg-red-200' : '';
                          // Resolve branch by name to derive available partai options for this column
                          const matchedBranch = branches.find(b => b.name && b.name.toLowerCase() === (branch || '').trim().toLowerCase());
                          const partaiMap = matchedBranch ? purchasesByBranch[matchedBranch.id] : null;
                          // Build options preserving the ORIGINAL casing from Pembelian (e.g. "ESN32" not "esn32")
                          const partaiOptions = partaiMap
                            ? Array.from(partaiMap.entries())
                                .sort((a, b) => a[1].localeCompare(b[1]))
                                .map(([key, label]) => ({ value: key, label }))
                            : [];
                          // Ensure current partai is present in the dropdown options (preserve casing as typed/stored)
                          const partaiLower = (partai || '').toLowerCase();
                          if (partai && !partaiOptions.some(o => o.value === partaiLower)) {
                            partaiOptions.unshift({ value: partaiLower, label: partai });
                          }
                          return (
                            <TableHead key={idx} className={`py-1 px-2 text-xs text-center ${branchBgClass}`} data-testid={`sales-column-header-${idx}`}>
                              <Input
                                value={branch}
                                onChange={(e) => handleBranchNameChange(idx, e.target.value)}
                                className={`h-6 text-xs text-center font-semibold border-0 bg-transparent p-0 ${isBranchInvalid ? 'text-red-800' : ''}`}
                                data-testid={`sales-branch-input-${idx}`}
                              />
                              <div className={`mt-1 ${partaiBgClass}`}>
                                <SearchableSelect
                                  value={partaiLower}
                                  onValueChange={(val) => {
                                    // Find original casing from options
                                    const opt = partaiOptions.find(o => o.value === val);
                                    handlePartaiNameChange(idx, opt ? opt.label : val);
                                  }}
                                  options={partaiOptions}
                                  placeholder={matchedBranch ? (partaiOptions.length === 0 ? 'Tidak ada partai' : 'Pilih partai') : 'Cabang invalid'}
                                  searchPlaceholder="Cari partai..."
                                  emptyText={matchedBranch ? 'Tidak ada partai di cabang ini' : 'Pilih cabang valid dulu'}
                                  className="w-full"
                                  triggerClassName={`h-6 text-[10px] px-1 py-0 font-normal ${isPartaiInvalid ? 'text-red-800 font-semibold' : 'text-slate-600'}`}
                                />
                              </div>
                            </TableHead>
                          );
                        })}
                        <TableHead className="py-1 px-2 text-xs text-center font-semibold bg-green-100">Total Berat (gr)</TableHead>
                        <TableHead className="py-1 px-2 text-xs text-center font-semibold bg-amber-100">Harga/gr</TableHead>
                        <TableHead className="py-1 px-2 text-xs text-center font-semibold bg-blue-100">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedTableData.materials.map((material, matIdx) => (
                        <TableRow key={matIdx}>
                          <TableCell className="py-1 px-2 text-xs font-medium bg-slate-50 sticky left-0 z-10 whitespace-nowrap">{material.name}</TableCell>
                          {material.weights.map((weight, wIdx) => (
                            <TableCell key={wIdx} className="py-1 px-2 text-xs text-center">
                              {weight ? weight.toLocaleString('id-ID') : '-'}
                            </TableCell>
                          ))}
                          <TableCell className="py-1 px-2 bg-green-50 text-xs text-center font-semibold">
                            {Math.round(calculateTotalKg(material.weights)).toLocaleString('id-ID')}
                          </TableCell>
                          <TableCell className="py-1 px-2 bg-amber-50 text-xs text-right">
                            {parseInt(material.price || 0).toLocaleString('id-ID')}
                          </TableCell>
                          <TableCell className="py-1 px-2 bg-blue-50 text-xs text-right font-semibold">
                            {Math.round(calculateSubtotal(material.weights, material.price)).toLocaleString('id-ID')}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total row */}
                      <TableRow className="border-t-2 border-teal-600 bg-teal-50">
                        <TableCell className="py-2 px-2 text-xs font-bold text-teal-800 sticky left-0 z-10 bg-teal-50 whitespace-nowrap">Total</TableCell>
                        {parsedTableData.branches.map((branch, idx) => (
                          <TableCell key={idx} className="py-2 px-2 text-xs text-center font-bold text-teal-800">
                            {Math.round(calculateTotalWeightPerPartai(idx)).toLocaleString('id-ID')}
                          </TableCell>
                        ))}
                        <TableCell className="py-2 px-2 bg-teal-100 text-xs text-center font-bold text-teal-800">
                          {parsedTableData.materials.reduce((sum, m) => sum + Math.round(calculateTotalKg(m.weights)), 0).toLocaleString('id-ID')}
                        </TableCell>
                        <TableCell className="py-2 px-2 bg-teal-100"></TableCell>
                        <TableCell className="py-2 px-2 bg-teal-100 text-xs text-right font-bold text-teal-800">
                          {Math.round(calculateGrandTotal()).toLocaleString('id-ID')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Additional Manual Rows */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-slate-700">Baris Tambahan</Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={handleAddRow}
                      className="h-7 text-xs border-teal-500 text-teal-700 hover:bg-teal-50"
                      data-testid="add-row-btn"
                    >
                      <Plus size={14} className="mr-1" />
                      Tambah Baris
                    </Button>
                  </div>
                  {additionalRows.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <Table className="text-xs">
                        <TableHeader>
                          <TableRow className="bg-amber-50">
                            <TableHead className="py-1 px-2 text-xs font-semibold w-12">No.</TableHead>
                            <TableHead className="py-1 px-2 text-xs font-semibold">Nama Cabang</TableHead>
                            <TableHead className="py-1 px-2 text-xs font-semibold">Partai</TableHead>
                            <TableHead className="py-1 px-2 text-xs font-semibold">Bahan</TableHead>
                            <TableHead className="py-1 px-2 text-xs font-semibold text-right">Nominal (Rp)</TableHead>
                            <TableHead className="py-1 px-2 text-xs font-semibold w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {additionalRows.map((row, idx) => (
                            <TableRow key={idx} data-testid={`additional-row-${idx}`}>
                              <TableCell className="py-1 px-2 text-xs text-center">{idx + 1}</TableCell>
                              <TableCell className="py-1 px-2">
                                <SearchableSelect
                                  value={row.branchName}
                                  onValueChange={(val) => handleAdditionalRowChange(idx, 'branchName', val)}
                                  options={branches.map(b => ({ value: b.name, label: b.name }))}
                                  placeholder="Pilih Cabang..."
                                  searchPlaceholder="Cari cabang..."
                                  emptyText="Tidak ditemukan"
                                  triggerClassName="h-7 text-xs"
                                />
                              </TableCell>
                              <TableCell className="py-1 px-2">
                                <Input
                                  type="text"
                                  value={row.partai}
                                  onChange={(e) => handleAdditionalRowChange(idx, 'partai', e.target.value)}
                                  placeholder="Partai"
                                  className="h-7 text-xs"
                                  data-testid={`additional-partai-${idx}`}
                                />
                              </TableCell>
                              <TableCell className="py-1 px-2">
                                <Input
                                  type="text"
                                  value={row.bahan || ''}
                                  onChange={(e) => handleAdditionalRowChange(idx, 'bahan', e.target.value)}
                                  placeholder="Nama Bahan"
                                  className="h-7 text-xs"
                                  data-testid={`additional-bahan-${idx}`}
                                />
                              </TableCell>
                              <TableCell className="py-1 px-2">
                                <Input
                                  type="text"
                                  value={row.nominal !== '' && row.nominal !== undefined ? (() => {
                                    const raw = String(row.nominal).replace(/\./g, '').replace(/[^\d-]/g, '');
                                    const num = parseInt(raw || '0');
                                    if (raw === '-' || raw === '-0') return '-';
                                    return num.toLocaleString('id-ID');
                                  })() : ''}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '');
                                    handleAdditionalRowChange(idx, 'nominal', val);
                                  }}
                                  placeholder="0"
                                  className="h-7 text-xs text-right"
                                  data-testid={`additional-nominal-${idx}`}
                                />
                              </TableCell>
                              <TableCell className="py-1 px-2 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveRow(idx)}
                                  data-testid={`remove-row-${idx}`}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 border-t pt-3">
                  {(() => {
                    const purchase = calculatePurchaseSummary();
                    const salesWeight = calculateTotalSalesWeight();
                    const diff = salesWeight - purchase.weight;
                    const fmt = (n) => Math.round(n).toLocaleString('id-ID');
                    return (
                      <>
                        <div
                          className={`rounded-lg px-5 py-3 border-2 ${diff < 0 ? 'bg-red-50 border-red-400' : 'bg-emerald-50 border-emerald-400'}`}
                          data-testid="weight-diff-card"
                        >
                          <div className={`text-sm font-semibold ${diff < 0 ? 'text-red-700' : 'text-emerald-700'}`}>+/- Berat</div>
                          <div className={`text-2xl font-bold ${diff < 0 ? 'text-red-800' : 'text-emerald-800'}`} data-testid="weight-diff-value">
                            {diff > 0 ? '+' : ''}{fmt(diff)} gr
                          </div>
                          <div className="text-[11px] text-slate-600 mt-0.5 leading-tight">
                            <span data-testid="weight-sales-value">Jual: {fmt(salesWeight)} gr</span>
                            <span className="mx-1">·</span>
                            <span data-testid="weight-purchase-value">Beli: {fmt(purchase.weight)} gr</span>
                          </div>
                        </div>
                        <div
                          className="bg-amber-100 border-2 border-amber-500 rounded-lg px-5 py-3 cursor-pointer hover:bg-amber-200 transition-colors"
                          onClick={() => setPurchaseDetailOpen(true)}
                          title="Klik untuk melihat rincian per partai"
                          data-testid="total-purchase-card"
                        >
                          <div className="text-sm font-semibold text-amber-800">Total Pembelian + Biaya</div>
                          <div className="text-2xl font-bold text-amber-900" data-testid="total-purchase-value">
                            Rp.&nbsp;{fmt(purchase.total)}
                          </div>
                          <div className="text-[11px] text-slate-600 mt-0.5 leading-tight" data-testid="total-purchase-breakdown">
                            Beli: Rp. {fmt(purchase.amount)} · Biaya: Rp. {fmt(purchase.biaya)}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  <div className="bg-teal-100 border-2 border-teal-600 rounded-lg px-6 py-3">
                    <div className="text-sm font-semibold text-teal-800">Total Penjualan</div>
                    <div className="text-2xl font-bold text-teal-900" data-testid="grand-total">
                      Rp.&nbsp;{Math.round(calculateCombinedGrandTotal()).toLocaleString('id-ID')}
                    </div>
                    {(() => {
                      const estLaba = calculateCombinedGrandTotal() - calculatePurchaseSummary().total;
                      return (
                        <div
                          className={`text-[11px] mt-0.5 leading-tight font-medium ${estLaba < 0 ? 'text-red-600' : 'text-emerald-700'}`}
                          data-testid="estimated-profit"
                        >
                          Estimasi laba: Rp. {Math.round(estLaba).toLocaleString('id-ID')}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex-shrink-0 border-t pt-3 mt-2">
              <div className="flex justify-between items-center w-full">
                <div className="flex gap-2">
                  {editingSaleIds.length > 0 && (
                    <>
                      <Button 
                        type="button" 
                        variant="destructive" 
                        onClick={handleDeleteBulkInvoice}
                        disabled={loading}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Hapus {importType === 'stok' ? 'Stok' : 'Invoice'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={handleConvertUlang}
                        disabled={loading}
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                      >
                        <FileSpreadsheet size={16} className="mr-2" />
                        Convert Ulang
                      </Button>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setPreviewDialogOpen(false)} disabled={loading}>
                    Batal
                  </Button>
                  <Button 
                    onClick={handleSubmitBulk} 
                    disabled={loading || hasInvalidPartaiInForm()} 
                    className="bg-teal-600 hover:bg-teal-700"
                    title={hasInvalidPartaiInForm() ? 'Ada partai yang tidak ditemukan di pembelian cabang. Perbaiki kolom merah dulu.' : ''}
                    data-testid="sales-submit-bulk-btn"
                  >
                    {loading ? 'Menyimpan...' : editingSaleIds.length > 0 ? 'Update Invoice' : 'Simpan Semua Penjualan'}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog rincian Total Pembelian + Biaya */}
        <Dialog open={purchaseDetailOpen} onOpenChange={setPurchaseDetailOpen}>
          <DialogContent className="max-w-3xl" data-testid="purchase-detail-dialog">
            <DialogHeader>
              <DialogTitle>Rincian Pembelian + Biaya per Partai</DialogTitle>
            </DialogHeader>
            {(() => {
              const summary = calculatePurchaseSummary();
              const q = purchaseDetailSearch.trim().toLowerCase();
              const visibleRows = q
                ? summary.rows.filter((r) =>
                    r.branchName.toLowerCase().includes(q) || r.partai.toLowerCase().includes(q)
                  )
                : summary.rows;
              const fmtN = (n) => Math.round(n).toLocaleString('id-ID');
              const sum = (field) => visibleRows.reduce((s, r) => s + r[field], 0);
              return (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={purchaseDetailSearch}
                      onChange={(e) => setPurchaseDetailSearch(e.target.value)}
                      placeholder="Cari nama cabang atau partai..."
                      className="pl-9 h-9 text-sm"
                      data-testid="purchase-detail-search"
                    />
                  </div>
                  <div className="overflow-auto max-h-[60vh]">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow className="bg-amber-50">
                        <TableHead className="text-xs font-semibold">Cabang</TableHead>
                        <TableHead className="text-xs font-semibold">Partai</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Berat (gr)</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Rp Beli</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Biaya</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleRows.map((row) => (
                        <TableRow key={row.key} data-testid={`purchase-detail-row-${row.branchName}-${row.partai}`}>
                          <TableCell className="whitespace-nowrap font-medium">{row.branchName}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.partai}</TableCell>
                          <TableCell className="text-right">{fmtN(row.weight)}</TableCell>
                          <TableCell className="text-right">Rp. {fmtN(row.amount)}</TableCell>
                          <TableCell className="text-right">Rp. {fmtN(row.biaya)}</TableCell>
                        </TableRow>
                      ))}
                      {visibleRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-500 py-4">
                            {q ? 'Tidak ada partai yang cocok dengan pencarian' : 'Belum ada partai yang dipilih'}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow className="bg-amber-100 border-t-2 border-amber-500 font-bold" data-testid="purchase-detail-total-row">
                        <TableCell colSpan={2} className="whitespace-nowrap">TOTAL{q ? ' (hasil pencarian)' : ''}</TableCell>
                        <TableCell className="text-right" data-testid="purchase-detail-total-weight">{fmtN(sum('weight'))}</TableCell>
                        <TableCell className="text-right" data-testid="purchase-detail-total-amount">Rp. {fmtN(sum('amount'))}</TableCell>
                        <TableCell className="text-right" data-testid="purchase-detail-total-biaya">Rp. {fmtN(sum('biaya'))}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </div>
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPurchaseDetailOpen(false)} data-testid="purchase-detail-close">Tutup</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Sale Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Detail Penjualan</DialogTitle>
            </DialogHeader>
            {selectedSale && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Tanggal</Label>
                    <p className="text-lg">{formatDate(selectedSale.sale_date)}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Partai</Label>
                    <p className="text-lg">{selectedSale.partai || '-'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-600">Produk</Label>
                  <p className="text-lg font-medium">{selectedSale.product_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Jumlah</Label>
                    <p className="text-lg">{selectedSale.quantity_kg.toFixed(2)} gr</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">Harga per gr</Label>
                    <p className="text-lg">{selectedSale.price_per_kg ? formatCurrency(selectedSale.price_per_kg) : '-'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-600">Pelanggan</Label>
                  <p className="text-lg">{selectedSale.customer_name || '-'}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-sm font-medium text-green-700">Total Penjualan</Label>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedSale.total_amount)}</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Tutup</Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? 'Menghapus...' : 'Hapus'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {!activePeriod && (
        <Card className="border-l-4 border-l-yellow-500 bg-yellow-50">
          <CardContent className="pt-6">
            <p className="text-yellow-800">Tidak ada periode aktif. Buat periode baru di menu Periode untuk mulai mencatat transaksi.</p>
          </CardContent>
        </Card>
      )}

      {sales.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <TrendingUp size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">Belum ada data penjualan.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp size={20} />
                Penjualan Aktif : {branchName}
                <span className="text-sm font-normal text-slate-500">({normalSales.length} penjualan)</span>
              </CardTitle>
              {selectedSales.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={() => setMoveDialogOpen(true)}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                  data-testid="move-sale-to-period-btn"
                >
                  <ArrowRightLeft size={16} className="mr-2" />
                  Pindahkan ke Periode Lain ({selectedSales.length})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto" id="sales-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedSales.length === normalSales.length && normalSales.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Partai</TableHead>
                    <TableHead>Nama Bahan</TableHead>
                    <TableHead>Berat (gr)</TableHead>
                    <TableHead>Harga/gr</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Keterangan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Group sales by partai
                    const groupedByPartai = normalSales.reduce((acc, sale) => {
                      const partai = sale.partai || 'Tanpa Partai';
                      if (!acc[partai]) acc[partai] = [];
                      acc[partai].push(sale);
                      return acc;
                    }, {});

                    return Object.entries(groupedByPartai).map(([partai, partaiSales]) => {
                      const partaiTotal = partaiSales.reduce((sum, sale) => sum + sale.total_amount, 0);
                      const partaiTotalKg = partaiSales.reduce((sum, sale) => sum + sale.quantity_kg, 0);
                      
                      return (
                        <React.Fragment key={partai}>
                          {/* Partai Header */}
                          <TableRow className="bg-teal-50 border-t-2 border-teal-300">
                            <TableCell colSpan={8} className="font-bold text-teal-700">
                              <div className="grid grid-cols-3 items-center">
                                <span className="font-semibold text-teal-800 justify-self-start">PARTAI: {partai}</span>
                                <span className="justify-self-center text-blue-600 font-bold text-lg">{branches.find(b => b.id === partaiSales[0]?.branch_id)?.name || partaiSales[0]?.branch_name || branchName}</span>
                                <div className="flex items-center gap-4 justify-self-end">
                                  <span className="font-semibold text-slate-700">Total: {Math.round(partaiTotalKg).toLocaleString('id-ID')} gr</span>
                                  <span className="font-semibold text-green-600">{formatCurrency(partaiTotal)}</span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Sales in this partai - Group by invoice */}
                          {(() => {
                            // Group by invoice_id (or date+customer for old data or stok)
                            const groupedByInvoice = {};
                            partaiSales.forEach(sale => {
                              let invoiceKey;
                              if (sale.keterangan === 'Stok') {
                                // Each stok entry is separate
                                invoiceKey = sale.id;
                              } else if (sale.invoice_id) {
                                invoiceKey = sale.invoice_id;
                              } else {
                                // Fallback for old data: group by date + customer
                                const dateStr = sale.sale_date?.split('T')[0] || 'no-date';
                                invoiceKey = `${dateStr}_${sale.customer_account_id || 'no-customer'}`;
                              }
                              
                              if (!groupedByInvoice[invoiceKey]) {
                                groupedByInvoice[invoiceKey] = [];
                              }
                              groupedByInvoice[invoiceKey].push(sale);
                            });

                            return Object.values(groupedByInvoice).map((invoiceSales, invoiceIdx) => {
                              // Sort by sort_order (original import order) if available, fallback to product_name
                              invoiceSales.sort((a, b) => {
                                // If both have sort_order, use it
                                if (a.sort_order !== null && a.sort_order !== undefined && 
                                    b.sort_order !== null && b.sort_order !== undefined) {
                                  return a.sort_order - b.sort_order;
                                }
                                // Fallback to product name for old data without sort_order
                                return (a.product_name || '').localeCompare(b.product_name || '');
                              });
                              
                              return invoiceSales.map((sale, saleIdx) => (
                                <TableRow 
                                  key={sale.id} 
                                  data-testid={`sale-row-${sale.id}`}
                                  className={`cursor-pointer transition-colors ${
                                    sale.keterangan === 'Stok' 
                                      ? 'bg-yellow-50 hover:bg-yellow-100' 
                                      : 'hover:bg-blue-50'
                                  }`}
                                  onClick={(e) => {
                                    console.log('TableRow onClick triggered', {
                                      target: e.target,
                                      type: e.target.type,
                                      saleId: sale.id,
                                      keterangan: sale.keterangan
                                    });
                                    
                                    // Don't open dialog if clicking checkbox
                                    if (e.target.type === 'checkbox' || e.target.closest('[role="checkbox"]')) {
                                      console.log('Checkbox clicked, ignoring row click');
                                      return;
                                    }
                                    
                                    console.log('Calling handleViewSale...');
                                    handleViewSale(sale);
                                  }}
                                >
                                  {saleIdx === 0 ? (
                                    <>
                                      {/* First column: Checkbox or Import Stok label */}
                                      {sale.keterangan === 'Stok' && sale.customer_name?.includes('Transfer Stok ke') ? (
                                        // Import Stok: Just a label, no stopPropagation
                                        <TableCell 
                                          rowSpan={invoiceSales.length} 
                                          className="border-r align-top"
                                        >
                                          <span className="text-xs text-orange-600 font-semibold">
                                            Import Stok
                                          </span>
                                        </TableCell>
                                      ) : (
                                        // Normal sale: Checkbox with stopPropagation
                                        <TableCell 
                                          rowSpan={invoiceSales.length} 
                                          className="border-r align-top"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Checkbox
                                            checked={selectedSales.includes(sale.id)}
                                            onCheckedChange={() => toggleSelectSale(sale.id)}
                                          />
                                        </TableCell>
                                      )}
                                      <TableCell rowSpan={invoiceSales.length} className="border-r align-top">
                                        {formatDate(sale.sale_date)}
                                      </TableCell>
                                      <TableCell rowSpan={invoiceSales.length} className="border-r align-top">
                                        {sale.partai || '-'}
                                      </TableCell>
                                    </>
                                  ) : null}
                                  <TableCell className="font-medium">{sale.product_name}</TableCell>
                                  <TableCell className="text-right">{Math.round(sale.quantity_kg).toLocaleString('id-ID')}</TableCell>
                                  <TableCell className="text-right">{sale.price_per_kg ? formatCurrency(sale.price_per_kg) : ''}</TableCell>
                                  <TableCell className="text-right font-bold text-green-600">{sale.total_amount ? formatCurrency(sale.total_amount) : ''}</TableCell>
                                  <TableCell className="text-slate-600">
                                    {sale.keterangan === 'Stok' && sale.customer_name?.includes('Transfer Stok ke') ? (
                                      <div className="flex items-center gap-2">
                                        <span>{sale.keterangan}</span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 px-2 text-xs border-orange-500 text-orange-600 hover:bg-orange-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            console.log('Button clicked for Import Stok');
                                            handleViewImportStok(sale);
                                          }}
                                        >
                                          Lihat Detail
                                        </Button>
                                      </div>
                                    ) : (
                                      sale.keterangan || '-'
                                    )}
                                  </TableCell>
                                </TableRow>
                              ));
                            });
                          })()}
                        </React.Fragment>
                      );
                    });
                  })()}
                </TableBody>
                {normalSales.length > 0 && (() => {
                  const grandTotalKg = normalSales.reduce((s, x) => s + (x.quantity_kg || 0), 0);
                  const grandTotalRp = normalSales.reduce((s, x) => s + (x.total_amount || 0), 0);
                  return (
                    <tfoot>
                      <tr className="bg-teal-100 border-t-2 border-teal-600 font-bold" data-testid="sales-grand-total-row">
                        <td colSpan={4} className="px-3 py-2 text-right text-sm text-teal-900">TOTAL</td>
                        <td className="px-3 py-2 text-sm text-teal-900" data-testid="sales-grand-total-gr">
                          {Math.round(grandTotalKg).toLocaleString('id-ID')} gr
                        </td>
                        <td></td>
                        <td className="px-3 py-2 text-sm text-green-700" data-testid="sales-grand-total-rp">
                          {formatCurrency(grandTotalRp)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabel 2: Penjualan dari Jadikan Stok */}
      {convertedSales.length > 0 && (
        <Card className="mt-6">
          <CardHeader className="bg-amber-50">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <FileSpreadsheet size={20} />
              Penjualan dari Transfer Stok
              <span className="text-sm font-normal">({convertedSales.length} penjualan)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Tanggal</TableHead>
                    <TableHead className="w-[150px]">Customer</TableHead>
                    <TableHead className="w-[80px]">Partai</TableHead>
                    <TableHead className="w-[150px]">Nama Bahan</TableHead>
                    <TableHead className="w-[80px] text-right">Berat (gr)</TableHead>
                    <TableHead className="w-[100px] text-right">Harga/gr</TableHead>
                    <TableHead className="w-[100px] text-right">Total</TableHead>
                    <TableHead className="w-[120px]">Keterangan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Group converted sales by partai
                    const groupedByPartai = convertedSales.reduce((acc, sale) => {
                      const partai = sale.partai || 'Tanpa Partai';
                      if (!acc[partai]) acc[partai] = [];
                      acc[partai].push(sale);
                      return acc;
                    }, {});

                    return Object.entries(groupedByPartai).map(([partai, partaiSales]) => {
                      const partaiTotal = partaiSales.reduce((sum, sale) => sum + sale.total_amount, 0);
                      const partaiTotalKg = partaiSales.reduce((sum, sale) => sum + sale.quantity_kg, 0);
                      
                      return (
                        <React.Fragment key={partai}>
                          {/* Partai Header */}
                          <TableRow className="bg-amber-50 border-t-2 border-amber-300">
                            <TableCell colSpan={8} className="font-bold text-amber-700">
                              <div className="grid grid-cols-3 items-center">
                                <span className="justify-self-start">PARTAI: {partai} ({partaiSales.length} penjualan | Total: Rp.&nbsp;{partaiTotal.toLocaleString('id-ID')})</span>
                                <span className="justify-self-center text-blue-600 font-bold text-lg">{branches.find(b => b.id === partaiSales[0]?.branch_id)?.name || partaiSales[0]?.branch_name || branchName}</span>
                                <span></span>
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Sales in this partai */}
                          {partaiSales.map((sale) => (
                            <TableRow 
                              key={sale.id}
                              className="cursor-pointer hover:bg-amber-100 transition-colors"
                              onClick={() => handleViewImportStok(sale)}
                            >
                              <TableCell>
                                {new Date(sale.sale_date).toLocaleDateString('id-ID')}
                              </TableCell>
                              <TableCell className="text-xs">
                                {sale.customer_name}
                              </TableCell>
                              <TableCell>{sale.partai || '-'}</TableCell>
                              <TableCell>{sale.product_name}</TableCell>
                              <TableCell className="text-right">
                                {sale.quantity_kg.toLocaleString('id-ID')}
                              </TableCell>
                              <TableCell className="text-right">
                                Rp.&nbsp;{sale.price_per_kg.toLocaleString('id-ID')}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-amber-600">
                                Rp.&nbsp;{sale.total_amount.toLocaleString('id-ID')}
                              </TableCell>
                              <TableCell className="text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    Transfer Stok
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 px-2 text-xs border-orange-500 text-orange-600 hover:bg-orange-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewImportStok(sale);
                                    }}
                                  >
                                    Lihat Detail
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    });
                  })()}
                  
                  {/* Total Row */}
                  {convertedSales.length > 0 && (
                    <TableRow className="bg-amber-50 border-t-2 border-amber-600">
                      <TableCell colSpan={6} className="font-bold text-amber-800">
                        TOTAL PENJUALAN TRANSFER STOK
                      </TableCell>
                      <TableCell className="font-bold text-amber-800 text-right">
                        Rp.&nbsp;{convertedSales.reduce((sum, s) => sum + s.total_amount, 0).toLocaleString('id-ID')}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Move to Period Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <ArrowRightLeft size={20} />
              Pindahkan Penjualan ke Periode Lain
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Memindahkan {selectedSales.length} penjualan yang dipilih ke periode lain. 
              Penjualan akan dihapus dari periode saat ini dan total rupiah akan dicatat di activity log untuk tracking.
            </p>
            <div>
              <Label htmlFor="move_target_period_sale">Pilih Periode Tujuan (Hanya Periode Aktif) *</Label>
              <Select value={selectedTargetPeriod} onValueChange={setSelectedTargetPeriod}>
                <SelectTrigger id="move_target_period_sale">
                  <SelectValue placeholder="Pilih periode..." />
                </SelectTrigger>
                <SelectContent>
                  {activePeriods.length === 0 ? (
                    <SelectItem value="none" disabled>Tidak ada periode aktif lain</SelectItem>
                  ) : (
                    activePeriods.map(period => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setMoveDialogOpen(false);
              setSelectedTargetPeriod('');
            }}>
              Batal
            </Button>
            <Button 
              onClick={handleMoveToPeriod} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || !selectedTargetPeriod}
            >
              {loading ? 'Memindahkan...' : 'Pindahkan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Stok Detail Dialog */}
      <Dialog open={importStokDetailOpen} onOpenChange={setImportStokDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Detail Import Stok</DialogTitle>
          </DialogHeader>
          {selectedImportStok && (
            <div className="space-y-4 overflow-y-auto max-h-[70vh]">
              {/* Transfer Info */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Informasi Transfer</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-600">Periode Asal:</span>
                    <span className="ml-2 font-medium">{selectedImportStok.transfer.description?.split('from ')[1]?.split(' to ')[0] || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Periode Tujuan:</span>
                    <span className="ml-2 font-medium">{selectedImportStok.transfer.description?.split('to ')[1] || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total Items:</span>
                    <span className="ml-2 font-medium">{selectedImportStok.sales.length} item</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total Nilai:</span>
                    <span className="ml-2 font-medium text-green-600">
                      {formatCurrency(selectedImportStok.transfer.total_amount || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sales in Current Period */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Penjualan (Periode Asal)</h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="text-xs">Partai</TableHead>
                        <TableHead className="text-xs">Nama Bahan</TableHead>
                        <TableHead className="text-xs text-right">Berat (gr)</TableHead>
                        <TableHead className="text-xs text-right">Harga/gr</TableHead>
                        <TableHead className="text-xs text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedImportStok.sales.map((sale) => (
                        <TableRow key={sale.id} className="text-xs">
                          <TableCell>{sale.partai || '-'}</TableCell>
                          <TableCell>{sale.product_name}</TableCell>
                          <TableCell className="text-right">{Math.round(sale.quantity_kg).toLocaleString('id-ID')}</TableCell>
                          <TableCell className="text-right">{sale.price_per_kg ? formatCurrency(sale.price_per_kg) : ''}</TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            {sale.total_amount ? formatCurrency(sale.total_amount) : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50 font-semibold">
                        <TableCell colSpan={2} className="text-xs">TOTAL</TableCell>
                        <TableCell className="text-xs text-right">
                          {Math.round(selectedImportStok.sales.reduce((sum, s) => sum + s.quantity_kg, 0)).toLocaleString('id-ID')}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-xs text-right text-green-600">
                          {formatCurrency(selectedImportStok.sales.reduce((sum, s) => sum + s.total_amount, 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Purchases in Target Period */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Pembelian (Periode Tujuan)</h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="text-xs">Partai</TableHead>
                        <TableHead className="text-xs">Nama Bahan</TableHead>
                        <TableHead className="text-xs text-right">Berat (gr)</TableHead>
                        <TableHead className="text-xs text-right">Harga/gr</TableHead>
                        <TableHead className="text-xs text-center">Keterangan</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedImportStok.purchases.map((purchase) => (
                        purchase.items.map((item, idx) => (
                          <TableRow key={`${purchase.id}-${idx}`} className="text-xs">
                            {idx === 0 && (
                              <TableCell rowSpan={purchase.items.length}>{purchase.partai || '-'}</TableCell>
                            )}
                            <TableCell>{item.name}</TableCell>
                            <TableCell className="text-right">{Math.round(item.weight_kg).toLocaleString('id-ID')}</TableCell>
                            <TableCell className="text-right">{item.price_per_kg ? formatCurrency(item.price_per_kg) : ''}</TableCell>
                            <TableCell className="text-center">
                              <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-[10px] font-semibold">
                                {item.keterangan || '-'}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setImportStokDetailOpen(false)}
                >
                  Tutup
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleCancelImportStok}
                  disabled={loading}
                >
                  {loading ? 'Membatalkan...' : 'Batalkan Import Stok'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
